import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Stack, Chip, Avatar, TextField, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, Skeleton, Alert, Autocomplete, Button, Divider,
  MenuItem, Tabs, Tab, LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Fingerprint as FingerprintIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
  attendanceToday,
  attendanceUnlinked,
  attendanceLink,
  attendanceReport,
  hrListEmployees,
} from '../BaseURL';

const PRIMARY       = '#1A9B7F';
const PRIMARY_DARK  = '#127a63';
const PRIMARY_TINT  = 'rgba(26,155,127,0.08)';
const BORDER        = '#e5e9ef';

const REFRESH_INTERVAL_MS = 20000;

const initialsOf = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

const formatTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Attendance() {
  const [tab, setTab]               = useState('live'); // 'live' | 'report'
  const [date, setDate]             = useState(todayISO());
  const [scans, setScans]           = useState([]);
  const [summaries, setSummaries]   = useState([]);
  const [totalScans, setTotalScans] = useState(0);
  const [unlinked, setUnlinked]     = useState([]);
  const [employees, setEmployees]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');

  // Refetch every 20s so the dashboard is effectively live without the
  // complexity of websockets. Cheap on the DB — the queries are indexed.
  const load = useCallback(async () => {
    setError(null);
    try {
      const [tRes, uRes, eRes] = await Promise.all([
        axios.get(attendanceToday, { params: { date } }),
        axios.get(attendanceUnlinked),
        axios.get(hrListEmployees),
      ]);
      if (tRes.data?.success) {
        setScans(tRes.data.scans || []);
        setSummaries(tRes.data.summaries || []);
        setTotalScans(tRes.data.total_scans || 0);
      }
      if (uRes.data?.success) setUnlinked(uRes.data.unlinked || []);
      if (eRes.data?.success) setEmployees(eRes.data.employees || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  }, [date]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const filteredSummaries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summaries;
    return summaries.filter(s => {
      const name = (s.employee_name || '').toLowerCase();
      const dept = (s.department || '').toLowerCase();
      const uid  = String(s.device_user_id || '').toLowerCase();
      return name.includes(q) || dept.includes(q) || uid.includes(q);
    });
  }, [summaries, search]);

  const handleLink = async (row, employeeId) => {
    try {
      const res = await axios.post(attendanceLink, {
        employee_id: employeeId,
        device_serial: row.device_serial,
        device_user_id: row.device_user_id,
      });
      if (res.data?.success) {
        await Swal.fire({
          icon: 'success', title: 'Linked',
          text: 'Historical scans have been assigned to this employee.',
          timer: 1600, showConfirmButton: false,
        });
        load();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error', title: 'Link failed',
        text: err.response?.data?.message || err.message,
      });
    }
  };

  return (
    <Box sx={{ fontFamily: '"Inter","Segoe UI",Arial,sans-serif', p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* ── Page header ────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1.2px',
          textTransform: 'uppercase', color: PRIMARY, mb: 0.25,
        }}>
          Human Resources · Attendance
        </Typography>
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 700, color: '#0f172a',
          letterSpacing: '-0.4px', lineHeight: 1.2,
        }}>
          Biometric attendance
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.92rem', mt: 0.25 }}>
          {tab === 'live'
            ? 'Live scans from the ZKTeco terminal — refreshes every 20 seconds.'
            : 'Analyse presence and punctuality across any date range.'}
        </Typography>
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        mb: 2, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
      }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: PRIMARY, height: 3 } }}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none', fontWeight: 700, fontSize: '0.88rem',
              minHeight: 44, color: '#475569',
            },
            '& .Mui-selected': { color: PRIMARY_DARK },
          }}
        >
          <Tab value="live"   icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}  iconPosition="start" label="Live" />
          <Tab value="report" icon={<AssessmentIcon sx={{ fontSize: 18 }} />}  iconPosition="start" label="Report" />
        </Tabs>
      </Paper>

      {tab === 'report' ? (
        <ReportPanel employees={employees} />
      ) : (
        <LiveHeader date={date} setDate={setDate} onRefresh={load} />
      )}
      {tab === 'live' && (
        <>

      {/* ── KPI strip ─────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        <Kpi label="People on site today" value={summaries.length} accent={PRIMARY} />
        <Kpi label="Total scans"          value={totalScans}       accent="#475569" />
        <Kpi label="Unlinked device IDs"  value={unlinked.length}
             accent={unlinked.length > 0 ? '#d97706' : '#15803D'} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.75 }}>{error}</Alert>
      )}

      {/* ── Unlinked scans (only shown when there are any) ────── */}
      {unlinked.length > 0 && (
        <Paper elevation={0} sx={{
          p: 2.25, mb: 2.5,
          border: `1px solid ${BORDER}`,
          borderLeft: `4px solid #d97706`,
          borderRadius: 2.5, bgcolor: '#fffbeb',
        }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}
                 alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a' }}>
                {unlinked.length} unlinked scan source{unlinked.length !== 1 ? 's' : ''}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#78350f' }}>
                Somebody scanned but their device user-ID isn't tied to an employee record yet.
                Link each ID to the right person and past scans will be back-filled.
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1}>
            {unlinked.map((row) => (
              <UnlinkedRow
                key={`${row.device_serial}:${row.device_user_id}`}
                row={row}
                employees={employees}
                onLink={handleLink}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* ── Today's summary ───────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: 2.25, mb: 2.5, border: `1px solid ${BORDER}`,
        borderRadius: 2.5, bgcolor: '#fff',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
               alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a' }}>
            Who's on site — {date}
          </Typography>
          <TextField
            size="small"
            placeholder="Search by name, department, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
            }}
            sx={{
              width: { xs: '100%', md: 320 },
              '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
            }}
          />
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` } }}>
                <Th>Employee</Th>
                <Th>Department</Th>
                <Th>First scan</Th>
                <Th>Last scan</Th>
                <Th>Time on site</Th>
                <Th>Scans</Th>
                <Th>Status</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredSummaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 5, textAlign: 'center', border: 'none' }}>
                    <PersonIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                      {search ? 'No one matches that search.' : 'No scans recorded for this date yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSummaries.map(s => (
                  <TableRow key={s.key} hover>
                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Avatar sx={{
                          width: 32, height: 32,
                          bgcolor: s.employee_id ? PRIMARY : '#f1f5f9',
                          color:   s.employee_id ? '#fff'  : '#94a3b8',
                          fontSize: '0.8rem', fontWeight: 700,
                        }}>
                          {s.employee_name ? initialsOf(s.employee_name)
                                           : <FingerprintIcon sx={{ fontSize: 16 }} />}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>
                            {s.employee_name || 'Unlinked scan'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8' }}>
                            device ID {s.device_user_id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Muted>{s.department || '—'}</Muted></TableCell>
                    <TableCell><TimeCell v={s.first_scan} /></TableCell>
                    <TableCell><TimeCell v={s.last_scan}  /></TableCell>
                    <TableCell>
                      <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                        label={s.duration_label}
                        size="small"
                        sx={{
                          height: 22, fontWeight: 700, fontSize: '0.72rem',
                          bgcolor: PRIMARY_TINT, color: PRIMARY_DARK,
                          '& .MuiChip-icon': { color: PRIMARY_DARK },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={s.scan_count} size="small" sx={{
                        height: 22, fontSize: '0.72rem', fontWeight: 700,
                        bgcolor: '#f1f5f9', color: '#475569',
                      }}/>
                    </TableCell>
                    <TableCell>
                      {s.checked_out ? (
                        <Chip label="Checked out" size="small" sx={{
                          height: 22, fontWeight: 700, fontSize: '0.72rem',
                          bgcolor: '#dbeafe', color: '#1e40af',
                        }}/>
                      ) : (
                        <Chip label="On site" size="small" sx={{
                          height: 22, fontWeight: 700, fontSize: '0.72rem',
                          bgcolor: '#dcfce7', color: '#15803D',
                        }}/>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Raw scan log ──────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: 2.25, border: `1px solid ${BORDER}`,
        borderRadius: 2.5, bgcolor: '#fff',
      }}>
        <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
          Raw scan log — {date}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` } }}>
                <Th>Time</Th>
                <Th>Employee</Th>
                <Th>Device ID</Th>
                <Th>Verify</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : scans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center', border: 'none' }}>
                    <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                      No scans yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                scans.map(s => (
                  <TableRow key={s.id}>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#334155' }}>
                      {formatTime(s.event_time)}
                    </TableCell>
                    <TableCell>
                      {s.current_name || s.logged_name
                        ? <span style={{ color: '#0f172a', fontWeight: 600 }}>{s.current_name || s.logged_name}</span>
                        : <span style={{ color: '#94a3b8' }}>Unlinked ({s.device_user_id})</span>}
                    </TableCell>
                    <TableCell><Muted>{s.device_user_id}</Muted></TableCell>
                    <TableCell><Muted>{verifyLabel(s.verify_mode)}</Muted></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </>
      )}
    </Box>
  );
}

// Extracted so the JSX in the main return stays readable. Only shown
// when the Live tab is active.
function LiveHeader({ date, setDate, onRefresh }) {
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
           alignItems={{ md: 'center' }} justifyContent="flex-end" sx={{ mb: 2 }}>
      <TextField
        type="date"
        size="small"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
      />
      <Tooltip title="Refresh now" arrow>
        <IconButton onClick={onRefresh} size="small" sx={{ color: PRIMARY }}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

// ── Local subcomponents ──────────────────────────────────────
function Kpi({ label, value, accent }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2.5,
      border: `1px solid ${BORDER}`, bgcolor: '#fff',
      minWidth: 160,
    }}>
      <Typography sx={{
        fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '1px', color: '#94a3b8',
      }}>{label}</Typography>
      <Typography sx={{
        fontSize: '1.4rem', fontWeight: 800, color: accent, lineHeight: 1.1,
      }}>{value}</Typography>
    </Paper>
  );
}

function Th({ children }) {
  return (
    <TableCell sx={{
      fontWeight: 700, fontSize: '0.7rem', color: '#475569',
      textTransform: 'uppercase', letterSpacing: '0.4px', py: 1,
    }}>{children}</TableCell>
  );
}

function Muted({ children }) {
  return <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{children}</span>;
}

function TimeCell({ v }) {
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#334155' }}>
      {formatTime(v)}
    </span>
  );
}

function verifyLabel(m) {
  if (m == null) return '—';
  // ZKTeco verify_mode conventions (may vary slightly by firmware)
  if (m === 1)  return 'Fingerprint';
  if (m === 15) return 'Face';
  if (m === 4 || m === 11) return 'Password';
  if (m === 25) return 'Card';
  return `Mode ${m}`;
}

function UnlinkedRow({ row, employees, onLink }) {
  const [selected, setSelected] = useState(null);
  return (
    <Paper elevation={0} sx={{
      p: 1.5, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff',
      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
      alignItems: { md: 'center' }, gap: 1.5,
    }}>
      <Avatar sx={{ bgcolor: '#fef3c7', color: '#78350f' }}>
        <FingerprintIcon />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>
          Device ID {row.device_user_id}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#78350f' }}>
          {row.scan_count} scan{row.scan_count !== 1 ? 's' : ''} · last seen {formatTime(row.last_seen)}
        </Typography>
      </Box>
      <Autocomplete
        size="small"
        sx={{ minWidth: 280 }}
        options={employees.filter(e => e.is_active)}
        getOptionLabel={(e) => `${e.full_name}${e.department ? ` — ${e.department}` : ''}`}
        value={selected}
        onChange={(_, v) => setSelected(v)}
        renderInput={(params) => (
          <TextField {...params} placeholder="Pick an employee…"
                     sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }} />
        )}
      />
      <Button
        variant="contained" size="small"
        disabled={!selected}
        startIcon={<LinkIcon />}
        onClick={() => onLink(row, selected.id)}
        sx={{
          bgcolor: PRIMARY, textTransform: 'none', fontWeight: 700,
          borderRadius: 1.75, boxShadow: 'none',
          '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
        }}
      >
        Link
      </Button>
    </Paper>
  );
}

// ── Reports tab ────────────────────────────────────────────────
// Per-employee analytics across a date range. Late-arrival threshold is
// user-configurable so hospitals with different shift start times can
// still get meaningful "on-time vs late" numbers.
function ReportPanel({ employees }) {
  const defaultFrom = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);  // last 7 days including today
    return d.toISOString().slice(0, 10);
  };
  const [from, setFrom]                     = useState(defaultFrom());
  const [to, setTo]                         = useState(todayISO());
  const [lateThreshold, setLateThreshold]   = useState('08:30');
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [department, setDepartment]         = useState('');
  const [rows, setRows]                     = useState([]);
  const [meta, setMeta]                     = useState({ working_days: 0 });
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [search, setSearch]                 = useState('');

  // Distinct departments derived from the employee list — saves a
  // separate API call.
  const departments = useMemo(() => {
    const s = new Set();
    employees.forEach(e => { if (e.department) s.add(e.department); });
    return [...s].sort();
  }, [employees]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(attendanceReport, {
        params: { from, to, lateThreshold, includeWeekends, department: department || undefined },
      });
      if (res.data?.success) {
        setRows(res.data.rows || []);
        setMeta({
          working_days:    res.data.working_days,
          late_threshold:  res.data.late_threshold,
        });
      } else {
        setError(res.data?.message || 'Failed to load report');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, lateThreshold, includeWeekends, department]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.full_name, r.department, r.position, r.device_user_id]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [rows, search]);

  // Roll-up metrics for the KPI strip.
  const totals = useMemo(() => {
    const t = {
      headcount: rows.length,
      present:   0,
      late:      0,
      absent_days: 0,
      minutes:   0,
    };
    rows.forEach(r => {
      t.present     += r.days_present;
      t.late        += r.late_arrivals;
      t.absent_days += r.days_absent;
      t.minutes     += r.total_minutes_on_site;
    });
    return t;
  }, [rows]);

  const downloadCsv = () => {
    const headers = [
      'Employee','Department','Position','Biometric ID',
      'Days present','Days absent','On-time','Late',
      'Avg first scan','Avg last scan','Avg hours / day',
      'Total hours',
    ];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = filtered.map(r => [
      r.full_name, r.department || '', r.position || '', r.device_user_id || '',
      r.days_present, r.days_absent, r.on_time_arrivals, r.late_arrivals,
      r.avg_first_scan || '', r.avg_last_scan || '',
      r.avg_minutes_on_site != null ? (r.avg_minutes_on_site / 60).toFixed(2) : '',
      (r.total_minutes_on_site / 60).toFixed(2),
    ].map(escape).join(','));
    const csv = [headers.join(','), ...body].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Controls */}
      <Paper elevation={0} sx={{
        p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: 2.5, bgcolor: '#fff',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
               alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            type="date" size="small" label="From"
            InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
          />
          <TextField
            type="date" size="small" label="To"
            InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
          />
          <TextField
            type="time" size="small" label="Late after"
            InputLabelProps={{ shrink: true }}
            value={lateThreshold} onChange={(e) => setLateThreshold(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
          />
          <TextField
            select size="small" label="Department"
            value={department} onChange={(e) => setDepartment(e.target.value)}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
          >
            <MenuItem value="">All departments</MenuItem>
            {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Weekends"
            value={includeWeekends ? 'yes' : 'no'}
            onChange={(e) => setIncludeWeekends(e.target.value === 'yes')}
            sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
          >
            <MenuItem value="no">Exclude Sat/Sun</MenuItem>
            <MenuItem value="yes">Include Sat/Sun</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small" placeholder="Search employee or department"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
            }}
            sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
          />
          <Button
            variant="outlined" size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            sx={{
              borderColor: PRIMARY, color: PRIMARY, textTransform: 'none',
              fontWeight: 700, borderRadius: 1.75,
              '&:hover': { borderColor: PRIMARY_DARK, bgcolor: PRIMARY_TINT },
            }}
          >
            Export CSV
          </Button>
        </Stack>
      </Paper>

      {/* KPI strip */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Kpi label={`Working days (${meta.working_days || 0})`}
             value={`${from} → ${to}`} accent="#475569" />
        <Kpi label="Employees in scope"    value={totals.headcount} accent={PRIMARY} />
        <Kpi label="Total presence days"   value={totals.present}   accent="#15803D" />
        <Kpi label="Total absence days"    value={totals.absent_days}
             accent={totals.absent_days > 0 ? '#b91c1c' : '#475569'} />
        <Kpi label={`Late arrivals (after ${meta.late_threshold || lateThreshold})`}
             value={totals.late}
             accent={totals.late > 0 ? '#d97706' : '#15803D'} />
        <Kpi label="Total hours on site"
             value={(totals.minutes / 60).toFixed(1)}
             accent={PRIMARY_DARK} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.75 }}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{
        p: 2, border: `1px solid ${BORDER}`, borderRadius: 2.5, bgcolor: '#fff',
      }}>
        {loading && <LinearProgress sx={{ borderRadius: 1, mb: 1, '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` } }}>
                <Th>Employee</Th>
                <Th>Department</Th>
                <Th>Present</Th>
                <Th>Absent</Th>
                <Th>On-time</Th>
                <Th>Late</Th>
                <Th>Avg first scan</Th>
                <Th>Avg last scan</Th>
                <Th>Avg hrs / day</Th>
                <Th>Total hrs</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 5, textAlign: 'center', border: 'none' }}>
                    <TimelineIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                      No employees match the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(r => {
                  const presenceRatio = meta.working_days > 0
                    ? r.days_present / meta.working_days : 0;
                  const presenceColor =
                    presenceRatio >= 0.9 ? '#15803D'
                    : presenceRatio >= 0.6 ? '#d97706'
                    : '#b91c1c';
                  return (
                    <TableRow key={r.employee_id} hover>
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar sx={{
                            width: 30, height: 30,
                            bgcolor: r.device_user_id ? PRIMARY : '#f1f5f9',
                            color: r.device_user_id ? '#fff' : '#94a3b8',
                            fontSize: '0.75rem', fontWeight: 700,
                          }}>
                            {initialsOf(r.full_name)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>
                              {r.full_name}
                            </Typography>
                            {r.position && (
                              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                {r.position}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell><Muted>{r.department || '—'}</Muted></TableCell>
                      <TableCell>
                        <Chip label={r.days_present} size="small" sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 700,
                          bgcolor: `${presenceColor}20`, color: presenceColor,
                        }}/>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: r.days_absent > 0 ? '#b91c1c' : '#94a3b8', fontWeight: 600 }}>
                          {r.days_absent}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: '#15803D', fontWeight: 700 }}>{r.on_time_arrivals}</span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: r.late_arrivals > 0 ? '#d97706' : '#94a3b8', fontWeight: 700 }}>
                          {r.late_arrivals}
                        </span>
                      </TableCell>
                      <TableCell><TabularTime v={r.avg_first_scan} /></TableCell>
                      <TableCell><TabularTime v={r.avg_last_scan} /></TableCell>
                      <TableCell>
                        <TabularNum v={r.avg_minutes_on_site != null ? (r.avg_minutes_on_site / 60).toFixed(1) : null} />
                      </TableCell>
                      <TableCell>
                        <TabularNum v={(r.total_minutes_on_site / 60).toFixed(1)} bold />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

function TabularTime({ v }) {
  return <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#334155' }}>{v || '—'}</span>;
}
function TabularNum({ v, bold }) {
  return <span style={{
    fontVariantNumeric: 'tabular-nums',
    fontWeight: bold ? 800 : 600,
    color: '#0f172a',
  }}>{v || '—'}</span>;
}
